/**
 * The card on the guests' phones (docs/15-getraenkekarte.md, Karte im Netz mit QR-Code): layout D,
 * one column for a narrow screen. The app publishes the parts of `drinksCard` as master data
 * `card`/`web`; the server turns them into a page here. The fields come from a device, so they are
 * read field by field and every text is escaped: no device ever hands the server finished HTML.
 */
import { cardFootnotes, lineSize, ownAllergens } from "./drinks-card.ts";
import type { CardEntry, CardGroup, CardLine, CardPart, CardSection } from "./drinks-card.ts";
import { CARD_LANG_NATIVE, CARD_LANG_SHORT, CARD_LANGS, PHRASE_LIMITS, allergenShortIn, cardTranslator, filterWords, isCardLang, phraseKey } from "./card-lang.ts";
import { cardLetters, entriesGone, entryLetters, goneCss, letterAllergen } from "./allergen-filter.ts";
import type { CardLang, CardTranslator } from "./card-lang.ts";

/** The published card: what the app froze when „Veröffentlichen“ was pressed. */
export interface WebCard {
  publishedAt: number;
  house: string;
  parts: CardPart[];
  /** „list“: the allergens are on a list for the staff, not on the card. */
  allergens: "card" | "list";
  /**
   * The languages published besides German (F28), each with the house's own translations of the
   * card's texts as they were at „Veröffentlichen“ (`frozenTexts`); the base vocabulary is not in it.
   */
  texts?: Partial<Record<CardLang, Record<string, string>>>;
}

/** How the owner set up the card on the net; fields of the same master data. */
export interface WebCardSettings {
  /** Drinks without stock at the time of the call are left out (default on). */
  hideSoldOut: boolean;
  /** Taken off the net: the link says the card is not available. */
  offline: boolean;
  address: string;
  /** Link to the house's imprint; only https. */
  imprint: string;
  /** The allergen filter for guests on the card called directly (F29, Martin 04.11.2026: default off). */
  allergenFilter: boolean;
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
const text = (v: unknown, max = 2000): string | null => (typeof v === "string" ? v.slice(0, max) : null);
const int = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : null);
const list = (v: unknown, max: number): unknown[] => (Array.isArray(v) ? v.slice(0, max) : []);
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const words = (v: unknown, max = 40) => list(v, max).map((x) => text(x, 200)).filter((x): x is string => x !== null);

function lineOf(v: unknown): CardLine | null {
  const l = obj(v);
  const article = text(l.article, 80);
  if (article === null) return null;
  const out: CardLine = { article, listNo: text(l.listNo, 20), vintage: int(l.vintage), size: text(l.size, 60) ?? "", price: int(l.price) };
  if (int(l.ml) !== null) out.ml = int(l.ml)!;
  if (int(l.bottle) !== null) out.bottle = int(l.bottle)!;
  if (int(l.usual) !== null) out.usual = int(l.usual)!;
  return out;
}

function entryOf(v: unknown): CardEntry | null {
  const e = obj(v);
  const lines = list(e.lines, 50).map(lineOf).filter((l): l is CardLine => l !== null);
  const wine = text(e.wine, 200);
  if (!lines.length || wine === null) return null;
  return {
    producer: text(e.producer, 200) ?? "",
    wine,
    color: text(e.color, 40) ?? "",
    country: text(e.country, 80),
    region: text(e.region, 120),
    appellation: text(e.appellation, 120),
    grapes: words(e.grapes),
    taste: text(e.taste, 60),
    organic: e.organic === true,
    text: text(e.text),
    lines,
    notes: list(e.notes, 20).map(int).filter((n): n is number => n !== null && n > 0 && n < 100),
    allergens: words(e.allergens),
    ...(e.allergenFree === true ? { allergenFree: true } : {}),
    recipe: e.recipe === true,
    plain: e.plain === true,
    extra: words(e.extra, 10),
    quality: text(e.quality, 120),
  };
}

function groupOf(v: unknown): CardGroup {
  const g = obj(v);
  return { title: text(g.title, 120), entries: list(g.entries, 2000).map(entryOf).filter((e): e is CardEntry => e !== null) };
}

function sectionOf(v: unknown): CardSection {
  const s = obj(v);
  return { title: text(s.title, 120) ?? "", groups: list(s.groups, 200).map(groupOf) };
}

function partOf(v: unknown): CardPart {
  const p = obj(v);
  const part: CardPart = { title: text(p.title, 120) ?? "", sections: list(p.sections, 100).map(sectionOf) };
  if (text(p.key, 80) !== null) part.key = text(p.key, 80)!;
  if (p.open === true) part.open = true;
  if (text(p.note, 300) !== null) part.note = text(p.note, 300)!;
  if (Array.isArray(p.common)) part.common = words(p.common);
  return part;
}

/** The frozen translations, field by field: known languages only, strings only, within the limits. */
function textsOf(v: unknown): Partial<Record<CardLang, Record<string, string>>> | undefined {
  const all = obj(v);
  const out: Partial<Record<CardLang, Record<string, string>>> = {};
  for (const lang of CARD_LANGS) {
    if (!(lang in all)) continue;
    const own: Record<string, string> = {};
    for (const [k, t] of Object.entries(obj(all[lang])).slice(0, PHRASE_LIMITS.count)) {
      const value = text(t, PHRASE_LIMITS.text);
      if (value) own[phraseKey(k.slice(0, PHRASE_LIMITS.key))] = value;
    }
    out[lang] = own;
  }
  return Object.keys(out).length ? out : undefined;
}

/** The published card from the fields of master data `card`/`web`, or null: never published. */
export function webCardOf(fields: Record<string, unknown> | null | undefined): WebCard | null {
  if (!fields || int(fields.publishedAt) === null || !Array.isArray(fields.parts)) return null;
  const card: WebCard = {
    publishedAt: int(fields.publishedAt)!,
    house: text(fields.house, 120) ?? "",
    parts: list(fields.parts, 50).map(partOf),
    allergens: fields.allergens === "list" ? "list" : "card",
  };
  const texts = textsOf(fields.texts);
  if (texts) card.texts = texts;
  return card;
}

/** The languages a published card offers besides German. */
export const webCardLangs = (card: WebCard | null): CardLang[] => CARD_LANGS.filter((l) => card?.texts?.[l] !== undefined);

/** Only an address that leaves for a web page: no script, no other scheme. */
export const imprintLink = (v: unknown): string => {
  const s = typeof v === "string" ? v.trim() : "";
  if (!/^https:\/\/[^\s"'<>]+$/i.test(s) || s.length > 300) return "";
  try {
    return new URL(s).protocol === "https:" ? s : "";
  } catch {
    return "";
  }
};

export function webCardSettingsOf(fields: Record<string, unknown> | null | undefined): WebCardSettings {
  const f = fields ?? {};
  return { hideSoldOut: f.hideSoldOut !== false, offline: f.offline === true, address: text(f.address, 300)?.trim() ?? "", imprint: imprintLink(f.imprint), allergenFilter: f.allergenFilter === true };
}

/** The card without the lines whose drink is drunk up; what is left empty goes too. */
export function withoutSoldOut(parts: CardPart[], soldOut: (article: string) => boolean): CardPart[] {
  return parts
    .map((p) => ({
      ...p,
      sections: p.sections
        .map((s) => ({
          ...s,
          groups: s.groups.map((g) => ({ ...g, entries: g.entries.map((e) => ({ ...e, lines: e.lines.filter((l) => !soldOut(l.article)) })).filter((e) => e.lines.length > 0) })).filter((g) => g.entries.length > 0),
        }))
        .filter((s) => s.groups.length > 0),
    }))
    .filter((p) => p.sections.length > 0);
}

/** Prices with the euro sign and always with cents, so they stand one below the other. */
const euro = (cents: number | null) => (cents === null ? "" : `${(cents / 100).toFixed(2).replace(".", ",")} €`);
const isOpen = (e: CardEntry) => e.lines[0]?.ml !== undefined;
/** The facts line; the country only where the group above does not already name it. */
const facts = (e: CardEntry, p: CardPart, allergens: boolean, group: string | null, t: CardTranslator) =>
  [
    ...(e.extra ?? []).map(t.text),
    [e.region, e.country === group ? null : e.country && t.text(e.country)].filter(Boolean).join(", "),
    e.quality ?? null,
    e.grapes.join(", "),
    e.taste && e.taste !== "trocken" ? t.text(e.taste) : null,
    e.organic ? t.words.organic : null,
    allergens && ownAllergens(e, p).length ? `${t.words.contains} ${ownAllergens(e, p).map(t.allergen).join(", ")}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

/** The line under a part's title: its note and, with the allergens on the card, what all its drinks contain. */
const partNote = (p: CardPart, allergens: boolean, t: CardTranslator) =>
  [p.note ? t.text(p.note) : null, allergens && p.common?.length ? `${t.words.Contains} ${p.common.map(t.allergen).join(", ")}` : null].filter(Boolean).join(" · ");

/** One drink: its number, the name with facts and card text, then one row per offer with the price. */
function entry(e: CardEntry, p: CardPart, allergens: boolean, group: string | null, t: CardTranslator, cls = ""): string {
  const open = isOpen(e) || e.plain === true;
  // One number for the drink when its offers share it (or it is open); else each offer shows its own.
  const shared = open || new Set(e.lines.map((l) => l.listNo)).size === 1;
  const no = shared ? (e.lines[0]!.listNo ?? "") : "";
  const year = isOpen(e) && e.lines[0]!.vintage !== null ? ` ${e.lines[0]!.vintage}` : "";
  const marks = e.notes?.length ? `<sup>${e.notes.join(",")}</sup>` : "";
  // A wine keeps its name; another drink only takes the house's own translation of it.
  const wine = e.plain ? t.name(e.wine) : e.wine;
  const name = e.producer ? `<b>${esc(wine)}${year}</b>${marks}<span class="prod">${esc(e.producer)}</span>` : `<b>${esc(wine)}${year}</b>${marks}`;
  const f = facts(e, p, allergens, group, t);
  const card = e.text && (!isOpen(e) || e.plain) ? `<p class="txt">${esc(t.text(e.text))}</p>` : "";
  const offers = e.lines
    .map((l) => {
      const label = [open ? null : l.vintage !== null ? String(l.vintage) : null, lineSize(l) ? t.size(lineSize(l)) : null].filter(Boolean).join(" · ");
      return `<li><span class="no">${shared ? "" : esc(l.listNo ?? "")}</span><span class="lbl">${esc(label)}</span><span class="pr">${euro(l.price)}</span></li>`;
    })
    .join("");
  return `<article class="e${cls}"><div class="head"><span class="no">${esc(no)}</span><div class="nm">${name}${f ? `<div class="f">${esc(f)}</div>` : ""}${card}</div></div><ul class="of">${offers}</ul></article>`;
}

const CSS = `
:root { --bg: #fbf8f4; --ink: #2a2522; --muted: #7d726a; --line: #e4dad1; --accent: #7b2d3a; --nav: rgba(251,248,244,0.96); }
@media (prefers-color-scheme: dark) { :root { --bg: #1d1a18; --ink: #ece4dc; --muted: #a59a91; --line: #3a3430; --accent: #e08a97; --nav: rgba(29,26,24,0.96); } }
* { box-sizing: border-box; }
html { -webkit-text-size-adjust: 100%; scroll-padding-top: 3.6rem; }
body { margin: 0; background: var(--bg); color: var(--ink); font: 17px/1.45 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; }
main { max-width: 40rem; margin: 0 auto; padding: 0 16px 2rem; }
header { text-align: center; padding: 1.6rem 16px 0.8rem; }
header .house { font-size: 1.9rem; letter-spacing: 0.06em; font-variant: small-caps; line-height: 1.15; overflow-wrap: anywhere; }
header .sub { font: 0.75rem system-ui, sans-serif; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin-top: 0.3rem; }
nav { position: sticky; top: 0; z-index: 1; background: var(--nav); border-bottom: 1px solid var(--line); display: flex; gap: 0.4rem; overflow-x: auto; padding: 0.55rem 16px; scrollbar-width: none; }
nav::-webkit-scrollbar { display: none; }
nav a { flex: none; font: 0.9rem system-ui, sans-serif; color: var(--ink); text-decoration: none; border: 1px solid var(--line); border-radius: 999px; padding: 0.3rem 0.8rem; white-space: nowrap; }
h2 { font-size: 1.5rem; font-weight: normal; font-variant: small-caps; letter-spacing: 0.05em; color: var(--accent); margin: 1.8rem 0 0.2rem; }
.note { font-size: 0.85rem; font-style: italic; color: var(--muted); margin: 0 0 0.4rem; }
h3 { font-size: 1.15rem; font-weight: normal; border-bottom: 1px solid var(--line); padding-bottom: 0.2rem; margin: 1.3rem 0 0.4rem; }
h4 { font: 0.72rem system-ui, sans-serif; text-transform: uppercase; letter-spacing: 0.18em; color: var(--muted); margin: 1rem 0 0.2rem; }
.e { padding: 0.55rem 0; border-bottom: 1px dotted var(--line); }
.e:last-child { border-bottom: 0; }
.head, .of li { display: grid; grid-template-columns: var(--no) minmax(0, 1fr) auto; column-gap: 0.5rem; align-items: baseline; }
.head { grid-template-columns: var(--no) minmax(0, 1fr); }
body { --no: 2.2rem; }
body.unnumbered { --no: 0; }
body.unnumbered .head, body.unnumbered .of li { column-gap: 0; }
body.unnumbered .of li { column-gap: 0.5rem; grid-template-columns: 0 minmax(0, 1fr) auto; }
.no { color: var(--accent); font-variant-numeric: tabular-nums; font-size: 0.9rem; text-align: center; }
.nm { min-width: 0; overflow-wrap: anywhere; }
.nm b { font-weight: 600; }
.prod { display: block; font: 0.72rem system-ui, sans-serif; text-transform: uppercase; letter-spacing: 0.1em; color: var(--muted); }
.f { font-size: 0.85rem; color: var(--muted); }
.txt { margin: 0.25rem 0 0; font-size: 0.92rem; font-style: italic; }
.of { list-style: none; margin: 0.2rem 0 0; padding: 0; }
.lbl { color: var(--muted); font-size: 0.92rem; font-variant-numeric: tabular-nums; }
.pr { font-variant-numeric: tabular-nums; text-align: right; white-space: nowrap; }
sup { font-size: 0.65rem; color: var(--muted); margin-left: 0.1rem; }
footer { max-width: 40rem; margin: 0 auto; padding: 1.2rem 16px 2.5rem; border-top: 1px solid var(--line); font: 0.8rem/1.5 system-ui, sans-serif; color: var(--muted); text-align: center; }
footer p { margin: 0 0 0.5rem; }
footer a { color: inherit; }
nav.langs { position: static; max-width: 40rem; margin: 0 auto; padding: 0.4rem 16px 0; border: 0; background: none; justify-content: flex-end; align-items: center; flex-wrap: wrap; gap: 0 0.1rem; overflow: visible; font: 0.85rem system-ui, sans-serif; color: var(--muted); }
nav.langs a { border: 0; border-radius: 0; color: var(--accent); padding: 0.4rem 0.45rem; }
nav.langs b { color: var(--ink); padding: 0.4rem 0.45rem; }
.msg { max-width: 30rem; margin: 18vh auto 0; padding: 0 16px; text-align: center; }
.msg h1 { font-weight: normal; font-size: 1.6rem; margin: 0 0 0.6rem; }
.msg p { color: var(--muted); font-family: system-ui, sans-serif; font-size: 0.95rem; }
`;

/** How the page is asked for (F28): its language, the languages it offers, and whether it stands inside SpeiseKarte. */
export interface WebView {
  lang: CardLang | "de";
  offered: readonly CardLang[];
  /** In SpeiseKarte's frame (`?embed=1`): no switch of its own, SpeiseKarte has one above it. */
  embed?: boolean;
  /** The allergen filter for guests (F29): the house's switch, or SpeiseKarte's `filter=1`. */
  filter?: boolean;
}
const GERMAN: WebView = { lang: "de", offered: [] };

/** „DE · EN · ES“ at the top right, as links without a script; only the languages the card has. */
const switcher = (view: WebView) =>
  view.embed || !view.offered.length
    ? ""
    : `<nav class="langs" aria-label="Sprache · Language">${(["de", ...view.offered] as (CardLang | "de")[])
        .map((l) => (l === view.lang ? `<b aria-current="page">${CARD_LANG_SHORT[l]}</b>` : `<a href="?lang=${l}" hreflang="${l}" lang="${l}" title="${CARD_LANG_NATIVE[l]}">${CARD_LANG_SHORT[l]}</a>`))
        .join(" · ")}</nav>`;

const page = (title: string, body: string, bodyClass = "", lang: CardLang | "de" = "de", extra: { meta?: string; css?: string } = {}) =>
  `<!doctype html><html lang="${lang}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><meta name="robots" content="noindex"><meta name="referrer" content="no-referrer"><meta name="color-scheme" content="light dark">${extra.meta ?? ""}<title>${esc(title)}</title><style>${CSS}${extra.css ?? ""}</style></head><body${bodyClass ? ` class="${bodyClass}"` : ""}>${body}</body></html>`;

/** The page when there is no card to show: never published, taken off the net, or an unknown link. */
export function webCardMessage(title: string, text: string, house = "", view: WebView = GERMAN): string {
  return page(house ? `${house} · ${title}` : title, `${switcher(view)}<div class="msg">${house ? `<header><div class="house">${esc(house)}</div></header>` : ""}<h1>${esc(title)}</h1><p>${esc(text)}</p></div>`, "", view.lang);
}

/** The view a link asks for, kept to what the card has: a language it does not offer is German. */
export function webViewOf(card: WebCard | null, lang: string | undefined, embed = false, filter = false): WebView {
  const offered = webCardLangs(card);
  return { lang: isCardLang(lang) && offered.includes(lang) ? lang : "de", offered, embed, ...(filter ? { filter } : {}) };
}

/** A text as a CSS string, for `content`. */
const cssText = (s: string) => `"${s.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/</g, "\\3c ")}"`;

/**
 * The filter's CSS (F29, as SpeiseKarte's S63): a drink goes with any ticked letter it has; the line
 * at the top and „Allergene nicht angegeben“ while anything is ticked; groups, sections and parts by
 * their rules. Only where the browser knows `:has()`; else the fold stays hidden and the card is as before.
 */
function filterCss(letters: readonly string[], rules: readonly string[], unknown: string): string {
  return `
.af, .afon { display: none; }
@supports selector(:has(*)) {
.af { display: block; max-width: 40rem; margin: 0 auto 0.6rem; padding: 0 16px; font-family: system-ui, sans-serif; }
.af details { border: 1px solid var(--line); border-radius: 10px; }
.af summary { cursor: pointer; padding: 0.5rem 0.8rem; font-size: 0.92rem; font-weight: 600; color: var(--accent); }
.afb { display: grid; grid-template-columns: repeat(auto-fill, minmax(8.5rem, 1fr)); gap: 0 0.8rem; padding: 0 0.8rem 0.3rem; font-size: 0.92rem; }
.afb label { display: flex; gap: 0.45rem; align-items: center; padding: 0.3rem 0; }
.afb input { width: 1.1rem; height: 1.1rem; margin: 0; accent-color: var(--accent); }
.afl { margin: 0 0.8rem 0.2rem; font-size: 0.85rem; color: var(--muted); }
.afn { margin: 0.1rem 0.8rem 0.6rem; font-size: 0.8rem; color: var(--muted); }
.afon { position: sticky; top: 0; z-index: 2; justify-content: space-between; align-items: center; gap: 0.6rem; min-height: 2.4rem; padding: 0.35rem max(16px, calc((100% - 40rem) / 2 + 16px)); background: var(--accent); color: var(--bg); font: 600 0.9rem system-ui, sans-serif; }
.afon button { font: 600 0.85rem system-ui, sans-serif; color: var(--accent); background: var(--bg); border: 0; border-radius: 999px; padding: 0.25rem 0.8rem; cursor: pointer; }
body:has(.af input:checked) .afon { display: flex; }
body:has(.af input:checked) nav:not(.langs) { top: 2.4rem; }
html:has(.af input:checked) { scroll-padding-top: 6rem; }
body:has(.af input:checked) article.e.xu .nm::after { content: ${cssText(unknown)}; display: block; margin-top: 0.15rem; font: 600 0.8rem system-ui, sans-serif; color: var(--accent); }
${letters.map((l) => `body:has(#af-${l}:checked) article.e.x${l} { display: none; }`).join("\n")}
${rules.filter(Boolean).join("\n")}
}`;
}

/** Layout D: the published card as one page for a phone, in the language of the view (F28). */
export function cardWebHtml(card: WebCard, settings: Pick<WebCardSettings, "address" | "imprint">, view: WebView = GERMAN): string {
  const lang = view.lang !== "de" && card.texts?.[view.lang] ? view.lang : "de";
  const t = cardTranslator(lang, lang === "de" ? {} : card.texts![lang]);
  const w = t.words;
  const allergens = card.allergens !== "list";
  const parts = card.parts;
  const numbered = parts.some((p) => p.sections.some((s) => s.groups.some((g) => g.entries.some((e) => e.lines.some((l) => l.listNo)))));
  const name = parts.some((p) => p.key !== undefined && p.key !== "offen" && p.key !== "flasche" && p.key !== "wein") ? w.drinksList : w.wineList;
  // The allergen filter (F29): only with the allergens on the card and one of the 14 on it.
  const letters = view.filter && allergens ? cardLetters(parts) : [];
  const filter = letters.length > 0;
  const rules: string[] = [];
  /** A class for a heading or part that goes when all its drinks go; "" without the filter. */
  const gone = (entries: CardEntry[], name: string) => {
    if (!filter) return "";
    rules.push(goneCss(entriesGone(entries), `.${name}`));
    return ` class="${name}"`;
  };
  const marks = (e: CardEntry) => {
    if (!filter) return "";
    const l = entryLetters(e);
    return l === null ? " xu" : l.map((x) => ` x${x}`).join("");
  };
  const all = (p: CardPart) => p.sections.flatMap((s) => s.groups.flatMap((g) => g.entries));
  const nav = parts.length > 1 ? `<nav aria-label="${esc(w.parts)}">${parts.map((p, i) => `<a href="#t${i}"${filter ? ` class="p${i}"` : ""}>${esc(t.text(p.title))}</a>`).join("")}</nav>` : "";
  const body = parts
    .map((p, i) => {
      const note = partNote(p, allergens, t);
      const sections = p.sections
        .map((s, j) => {
          const head = s.title && s.title !== p.title ? `<h3${gone(s.groups.flatMap((g) => g.entries), `s${i}-${j}`)}>${esc(t.text(s.title))}</h3>` : "";
          return `${head}${s.groups.map((g, k) => `${g.title ? `<h4${gone(g.entries, `g${i}-${j}-${k}`)}>${esc(t.text(g.title))}</h4>` : ""}${g.entries.map((e) => entry(e, p, allergens, g.title, t, marks(e))).join("")}`).join("")}`;
        })
        .join("");
      return `<section id="t${i}"${gone(all(p), `p${i}`)}><h2>${esc(t.text(p.title))}</h2>${note ? `<p class="note">${esc(note)}</p>` : ""}${sections}</section>`;
    })
    .join("");
  const notes = cardFootnotes(parts).map((f) => `${f.n} ${t.additive(f.n)}`).join(" · ");
  const date = new Date(card.publishedAt).toLocaleDateString("de-DE", { timeZone: "Europe/Berlin" });
  const footer = [
    notes ? `<p>${esc(notes)}</p>` : "",
    allergens ? "" : `<p>${esc(w.askAllergens)}</p>`,
    `<p>${esc(w.prices)} · ${esc(w.vintages)} · ${esc(w.asOf)} ${esc(date)}</p>`,
    settings.address || settings.imprint ? `<p>${esc(card.house)}${settings.address ? ` · ${esc(settings.address)}` : ""}${settings.imprint ? ` · <a href="${esc(settings.imprint)}" rel="noopener noreferrer">${esc(w.imprint)}</a>` : ""}</p>` : "",
  ].join("");
  const content = parts.length ? body : `<div class="msg"><p>${esc(w.empty)}</p></div>`;
  // The filter's fold under the head, its line at the very top; no script, no form sent (F29).
  const fw = filterWords(lang);
  const bar = filter ? `<div class="afon" role="status"><span>${esc(fw.allergenFilterOn)}</span><button type="reset" form="af">${esc(fw.allergenFilterReset)}</button></div>` : "";
  const fold = filter
    ? `<form class="af" id="af"><details><summary>${esc(fw.allergenFilter)}</summary><p class="afl">${esc(fw.allergenFilterLead)}</p><div class="afb">${letters.map((l) => `<label><input type="checkbox" id="af-${l}"> ${esc(allergenShortIn(letterAllergen(l), lang) ?? l)}</label>`).join("")}</div><p class="afn">${esc(fw.note)}</p></details></form>`
    : "";
  const extra = filter ? { meta: '<meta name="allergen-filter" content="on">', css: filterCss(letters, rules, fw.allergensUnknown) } : {};
  return page(`${name} · ${card.house}`, `${bar}${switcher({ ...view, lang })}<header><div class="house">${esc(card.house)}</div><div class="sub">${esc(name)}</div></header>${fold}${nav}<main>${content}</main><footer>${footer}</footer>`, numbered ? "" : "unnumbered", lang, extra);
}
